import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { StatusDot } from "../components/chips";
import { LoadError, Loading } from "../components/page-state";
import { StageLegend, StageStrip } from "../components/stage-strip";
import { chaptersQuery, enemiesQuery, pagesQuery, skillsQuery } from "../lib/queries";

export const Route = createFileRoute("/")({ component: Overview });

/**
 * The overview is the ten chapters, each with its stage strip. The strip is
 * the memorable thing here: twenty cells that ARE the run, no prose needed.
 */
function Overview() {
	const chapters = useQuery(chaptersQuery);
	const enemies = useQuery(enemiesQuery);
	const skills = useQuery(skillsQuery);
	const pages = useQuery(pagesQuery);

	if (chapters.isPending) return <Loading what="Opening the codex" />;
	if (chapters.isError) return <LoadError error={chapters.error} retry={() => void chapters.refetch()} />;

	const list = chapters.data;
	const written = list.filter((c) => c.annotation.summary || c.annotation.lore).length;
	const finals = list.filter((c) => c.annotation.status === "final").length;
	const skillsInGame = skills.data?.filter((s) => s.implemented).length;

	return (
		<div className="space-y-8">
			<header className="max-w-2xl">
				<h1 className="text-4xl leading-tight">The run, chapter by chapter</h1>
				<p className="mt-3" style={{ color: "var(--color-ink-300)" }}>
					Ten chapters of twenty fixed stages. Numbers come straight from the game's data files; everything the team writes about
					them lives here. Pick a chapter to document its monsters, its boss and how it should feel.
				</p>
			</header>

			<dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
				<Stat label="Chapters documented" value={`${written} / ${list.length}`} hint={`${finals} final`} />
				<Stat label="Monster types" value={enemies.data ? String(enemies.data.length) : "…"} hint="in enemies.json" />
				<Stat label="Skills in the game" value={skillsInGame !== undefined ? `${skillsInGame} / ${skills.data?.length}` : "…"} hint="with a working effect" />
				<Stat label="Design pages" value={pages.data ? String(pages.data.length) : "…"} hint="team-written" />
			</dl>

			<section>
				<div className="mb-3 flex flex-wrap items-center justify-between gap-3">
					<h2 className="text-xl">Chapters</h2>
					<StageLegend />
				</div>
				<ol className="space-y-2">
					{list.map((ch) => (
						<li key={ch.id}>
							<Link
								to="/chapters/$id"
								params={{ id: ch.id }}
								className="panel block p-4 transition-colors hover:border-[var(--color-ink-600)] focus-visible:border-[var(--color-gold-500)]"
							>
								<div className="mb-3 flex items-baseline gap-3">
									<span className="num font-display text-2xl font-semibold" style={{ color: "var(--color-ink-400)" }}>
										{String(ch.index).padStart(2, "0")}
									</span>
									<span className="min-w-0 flex-1 truncate text-base font-medium">{ch.annotation.summary || ch.name}</span>
									<StatusDot status={ch.annotation.status} />
								</div>
								<StageStrip chapter={ch} />
							</Link>
						</li>
					))}
				</ol>
			</section>
		</div>
	);
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
	return (
		<div className="panel p-4">
			<dt className="text-xs" style={{ color: "var(--color-ink-300)" }}>
				{label}
			</dt>
			<dd className="num mt-1 font-display text-2xl font-semibold">{value}</dd>
			<dd className="text-xs" style={{ color: "var(--color-ink-400)" }}>
				{hint}
			</dd>
		</div>
	);
}
